import { List } from "@dropbox/dig-components/list";
import { Text } from "@dropbox/dig-components/typography";
import { Box, Stack } from "@dropbox/dig-foundations";
import { SuggestionTile } from "@mirage/mosaics/SuggestionTile/SuggestionTile";
import i18n from "@mirage/translations";
import { useSummarizableStackItems } from "../useSummarizableStackItems";
// Note: doing this since it seems like other mosaics allow imports from service-conversation already
// eslint-disable-next-line boundaries/element-types
import {
  buildConversationRouterState,
  CtaSource,
  SummarizeItemParams,
  type AskWithPromptMultiDocsParams,
} from "@mirage/service-conversation/utils/buildConversationRouterState";
import { useNavigate } from "react-router-dom";
import styles from "./StackQuerySuggestions.module.css";

import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_ChatSuggestion } from "@mirage/analytics/events/types/click_chat_suggestion";
import { PAP_Click_StackChat } from "@mirage/analytics/events/types/click_stack_chat";
import { PAP_Shown_DashQuerySuggestionPrompt } from "@mirage/analytics/events/types/shown_dash_query_suggestion_prompt";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import { useTheme } from "@mirage/service-settings/theming/useTheme";
import { DigTooltip } from "@mirage/shared/util/DigTooltip";
import { useSortedStacks } from "@mirage/stacks/hooks";
import { useReportStackPapEvent } from "@mirage/stacks/useReportStackPapEvent";
import { useEffect, useMemo } from "react";
import { convertStackItemToChatSource } from "../utils";
import { useStackQuerySuggestions } from "./useStackQuerySuggestions";

const DEFAULT_QUERY_SUGGESTION = {
  uuid: "stack_query_suggestions_default_query",
  text: i18n.t("summarize_this_stack"),
};

export const StackQuerySuggestions = ({ stackId }: { stackId?: string }) => {
  const allStacks = useSortedStacks();
  const isDarkMode = useTheme();
  const navigate = useNavigate();
  const summarizableItems = useSummarizableStackItems(0);
  const { reportPapEvent } = useMirageAnalyticsContext();
  const reportStackPapEvent = useReportStackPapEvent();

  const chatSources = useMemo(() => {
    return summarizableItems.map((item) =>
      convertStackItemToChatSource(item, allStacks ?? [], isDarkMode),
    );
  }, [isDarkMode, summarizableItems]);
  const documentIds = useMemo(() => {
    return summarizableItems.reduce((acc, item) => {
      if (item.uuid) {
        acc.push(item.uuid);
      }

      return acc;
    }, [] as string[]);
  }, [summarizableItems]);

  const { isLoading, querySuggestions } = useStackQuerySuggestions(documentIds);

  const dynamicSuggestions = useMemo(() => {
    return querySuggestions.filter(
      (suggestion) => !suggestion.isStaticSuggestion,
    );
  }, [querySuggestions]);

  useEffect(() => {
    dynamicSuggestions.forEach((suggestion) => {
      if (suggestion.text !== "") {
        reportStackPapEvent(
          PAP_Shown_DashQuerySuggestionPrompt({
            actionSurfaceComponent: "sidebar",
            dashActionSurface: "stack_details_page",
            featureLine: "stacks",
            predictionId: suggestion.predictionId,
          }),
        );
      }
    });
  }, [dynamicSuggestions]);

  function handleSuggestionClick(_suggestion: QuerySuggestionResult) {
    if (!stackId) return;

    if (_suggestion === DEFAULT_QUERY_SUGGESTION) {
      reportStackPapEvent(
        PAP_Click_StackChat({
          actionSurfaceComponent: "sidebar",
        }),
      );

      // If the default suggestion is clicked, we want to navigate to chat without a pre-built message
      const state = buildConversationRouterState(CtaSource.StackSummarize, {
        chatSources,
        scenarioMeta: {
          type: "stack",
          sharingId: stackId,
        },
      } satisfies SummarizeItemParams);

      navigate("/chat", {
        state,
      });
    } else {
      reportPapEvent(
        PAP_Click_ChatSuggestion({
          actionSurfaceComponent: "sidebar",
          dashActionSurface: "stack_details_page",
          featureLine: "stacks",
          predictionId: _suggestion.predictionId,
          promptType: "chat_suggestion",
        }),
      );

      // Otherwise, if the user chose a generated suggestion, navigate to chat with the suggestion text
      const state = buildConversationRouterState(
        CtaSource.StackSuggestedPrompt,
        {
          chatSources,
          message: _suggestion.text,
          scenarioMeta: {
            type: "stack",
            sharingId: stackId,
          },
        } satisfies AskWithPromptMultiDocsParams,
      );

      navigate("/chat", {
        state,
      });
    }
  }

  if (!documentIds.length) return null;

  return (
    <Stack className={styles.container} gap="Micro Small">
      <List.Item className={styles.title}>
        <Text color="standard" isBold>
          {i18n.t("stack_query_suggestions_title")}
        </Text>
      </List.Item>
      {dynamicSuggestions.map((suggestion) => (
        <SuggestionTile
          key={suggestion.uuid}
          size="small"
          text={suggestion.text}
          onClick={() => handleSuggestionClick(suggestion)}
          isLoading={isLoading}
          display="block"
          tileStyles={{
            borderRadius: "var(--dash-radius__component)",
          }}
          predictionId={suggestion.predictionId}
        />
      ))}
      <DigTooltip
        isPortaled
        openDelay={600}
        placement="top"
        title={i18n.t("summarize_this_stack_tooltip", {
          count: summarizableItems.length,
        })}
      >
        <Box>
          <SuggestionTile
            size="small"
            text={DEFAULT_QUERY_SUGGESTION.text}
            onClick={() => handleSuggestionClick(DEFAULT_QUERY_SUGGESTION)}
            isLoading={isLoading}
            display="block"
            tileStyles={{
              borderRadius: "var(--dash-radius__component)",
            }}
          />
        </Box>
      </DigTooltip>
    </Stack>
  );
};
